import { z } from 'zod'
import { idSchema, isoDateTimeSchema, optionalText } from './common.js'

export const FUEL_TYPES = ['petrol', 'diesel', 'hybrid', 'electric', 'other'] as const
export type FuelType = (typeof FUEL_TYPES)[number]
export const fuelTypeSchema = z.enum(FUEL_TYPES)

export const FUEL_TYPE_LABELS: Record<FuelType, string> = {
  petrol: 'Petrol',
  diesel: 'Diesel',
  hybrid: 'Hybrid',
  electric: 'Electric',
  other: 'Other',
}

/**
 * Free text that may legitimately be blank.
 *
 * EVERY identifying field is optional. A car often arrives before anyone has
 * written down its plate, and refusing to save the jobcard until the record is
 * complete stops the workshop doing the thing it actually needs to do — start
 * costing the work. Kept as an empty string rather than null so `Car` stays one
 * flat shape and `carLabel` has one job.
 */
const blankable = (max: number) => z.string().trim().max(max).default('')

export const carInputSchema = z.object({
  /** Nullable: a car may exist on a jobcard without joining anyone's garage. */
  clientId: idSchema.nullable().default(null),
  brand: blankable(60),
  model: blankable(60),
  year: z.number().int().min(1900).max(2100).nullable().default(null),
  vin: optionalText(32),
  color: optionalText(40),
  /** How staff usually find a car — but not always known when it arrives. */
  plate: blankable(20),
  engineSize: optionalText(20),
  fuelType: fuelTypeSchema.nullable().default(null),
})
export type CarInput = z.infer<typeof carInputSchema>

export const carSchema = carInputSchema.extend({
  id: idSchema,
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
})
export type Car = z.infer<typeof carSchema>

/**
 * The car as frozen onto a jobcard. Identical fields minus the identity ones,
 * because a jobcard must keep describing the vehicle as it was when the work
 * happened even if the car row is later corrected or sold on.
 */
export const carSnapshotSchema = z.object({
  brand: z.string(),
  model: z.string(),
  year: z.number().int().nullable(),
  vin: z.string().nullable(),
  color: z.string().nullable(),
  plate: z.string(),
  engineSize: z.string().nullable(),
  fuelType: fuelTypeSchema.nullable(),
})
export type CarSnapshot = z.infer<typeof carSnapshotSchema>

export function carSnapshotFrom(car: CarInput | Car): CarSnapshot {
  return {
    brand: car.brand,
    model: car.model,
    year: car.year,
    vin: car.vin,
    color: car.color,
    plate: car.plate,
    engineSize: car.engineSize,
    fuelType: car.fuelType,
  }
}

/**
 * A readable name for a car, however little is known about it.
 *
 * Every part is optional, so this drops empty pieces rather than rendering
 * " — " or a stray dash. A car with nothing filled in still needs a label,
 * because it appears in lists the moment the jobcard is saved.
 */
export function carLabel(car: Pick<CarSnapshot, 'brand' | 'model' | 'year' | 'plate'>): string {
  const description = [car.brand, car.model].filter(Boolean).join(' ').trim()
  const withYear = [description, car.year ? String(car.year) : ''].filter(Boolean).join(' ')
  const plate = car.plate?.trim()

  if (withYear && plate) return `${withYear} — ${plate}`
  if (withYear) return withYear
  if (plate) return plate
  return 'Vehicle (no details yet)'
}
